import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import usePageMeta from "@/lib/usePageMeta";
import SectionHead from "@/components/SectionHead";
import { Reveal } from "@/components/Reveal";
import { POSTS } from "@/lib/blogData";

export default function Blog() {
  usePageMeta(
    "Insights — Hiring & Recruitment Blog | Talentify",
    "Perspectives on IT hiring in India, leadership hiring and recruitment strategy from the Talentify team."
  );
  const [featured, ...rest] = POSTS;

  return (
    <main>
      <section className="relative overflow-hidden pb-24 pt-40">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(circle at 50% -20%, rgba(56,189,248,0.1), transparent 55%)" }} />
        <div className="relative mx-auto max-w-7xl px-6">
          <SectionHead
            overline="Insights"
            title="Perspectives on Hiring in India"
            sub="Practical thinking on recruitment, talent markets and building great teams."
            testid="blog-heading"
          />
          <Reveal>
            <Link
              to={`/blog/${featured.slug}`}
              data-testid="blog-featured-card"
              className="group mb-6 block overflow-hidden rounded-3xl border border-white/[0.08] bg-[#0A0F1D]/80 transition-[border-color] duration-500 hover:border-sky-400/30"
            >
              <div className="p-9 sm:p-12">
                <div className="flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em]">
                  <span className="rounded-full border border-sky-400/25 bg-sky-400/[0.07] px-3 py-1 text-sky-300">{featured.category}</span>
                  <span className="text-slate-500">{featured.date}</span>
                  <span className="flex items-center gap-1 text-slate-500"><Clock size={11} /> {featured.readTime}</span>
                </div>
                <h2 className="mt-5 max-w-3xl font-display text-2xl font-bold tracking-tight text-white transition-colors duration-300 group-hover:text-sky-300 sm:text-3xl">
                  {featured.title}
                </h2>
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-400">{featured.excerpt}</p>
                <span className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-sky-400">
                  Read article <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </Link>
          </Reveal>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {rest.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group flex h-full flex-col rounded-3xl border border-white/[0.08] bg-[#0A0F1D]/60 p-8 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-sky-400/30"
                >
                  <div className="flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em]">
                    <span className="rounded-full border border-indigo-400/25 bg-indigo-400/[0.07] px-3 py-1 text-indigo-300">{p.category}</span>
                    <span className="text-slate-500">{p.date}</span>
                    <span className="flex items-center gap-1 text-slate-500"><Clock size={11} /> {p.readTime}</span>
                  </div>
                  <h3 className="mt-4 font-display text-xl font-semibold tracking-tight text-white transition-colors duration-300 group-hover:text-sky-300">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{p.excerpt}</p>
                  <span className="mt-auto inline-flex items-center gap-2 pt-6 text-sm font-semibold text-sky-400">
                    Read article <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
